import { create } from 'apisauce'
import { notifyError } from '../utils/errorNotifier'

const api = create({
  baseURL: process.env.REACT_APP_API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
})

const publicApi = create({
  baseURL: process.env.REACT_APP_API_URL,
  headers: { 'Content-Type': 'application/json' }
})

// Public stats live at the API host root (/api/public/stats), not under /api/v1
const API_ROOT = (process.env.REACT_APP_API_URL || '').replace(/\/api\/v\d+\/?$/, '')
const publicStatsApi = create({
  baseURL: API_ROOT,
  headers: { 'Content-Type': 'application/json' },
})

export { publicApi }
export const apiInstance = api
export const publicApiInstance = publicApi

// ─── INTERCEPTOR SETUP ────────────────────────────────────────────
let requestInterceptorId = null
let responseInterceptorId = null
let publicResponseInterceptorId = null

export const setupApiInterceptors = (getToken) => {

  if (requestInterceptorId !== null) {
    api.axiosInstance.interceptors.request.eject(requestInterceptorId)
  }
  if (responseInterceptorId !== null) {
    api.axiosInstance.interceptors.response.eject(responseInterceptorId)
  }
  if (publicResponseInterceptorId !== null) {
    publicApi.axiosInstance.interceptors.response.eject(publicResponseInterceptorId)
  }

  requestInterceptorId = api.axiosInstance.interceptors.request.use(async (config) => {
    try {
      const token = await getToken()
      if (token) {
        config.headers.Authorization = `Bearer ${token}`
      }
    } catch (error) {
      console.error('Failed to get auth token:', error)
    }
    return config
  })

  const handleError = (error) => {
    const originalRequest = error.config

    if (error.response?.status === 401 && originalRequest && !originalRequest._retry) {
      originalRequest._retry = true

      return getToken({ skipCache: true })
        .then((freshToken) => {
          if (freshToken) {
            originalRequest.headers.Authorization = `Bearer ${freshToken}`
            return api.axiosInstance(originalRequest)
          }
          notifyError(error)
          window.location.href = '/'
          return Promise.reject(error)
        })
        .catch(() => {
          notifyError(error)
          window.location.href = '/'
          return Promise.reject(error)
        })
    }

    // 400/404 errors are expected (validation, no data yet, etc.) — don't show overlay
    if (error.response?.status === 404 || error.response?.status === 400) {
      return Promise.reject(error)
    }

    notifyError(error)
    return Promise.reject(error)
  }

  responseInterceptorId = api.axiosInstance.interceptors.response.use(
    (response) => response,
    handleError
  )

  publicResponseInterceptorId = publicApi.axiosInstance.interceptors.response.use(
    (response) => response,
    (error) => {
      const status = error.response?.status
      if (status !== 404 && status !== 400 && status !== 401) {
        notifyError(error)
      }
      return Promise.reject(error)
    }
  )
}

// Keep these for any legacy usage but they are no longer the
// primary auth mechanism
const setAuthToken = (token) => api.setHeader('Authorization', `Bearer ${token}`)
const removeAuthToken = () => api.deleteHeader('Authorization')

// ─── USER ENDPOINTS ───────────────────────────────────────────────
const fetchUserBySub = () => api.get(`/user/user-profile`)
const updateUserBySub = (userData) => api.put(`/user/user-updated`, userData)
const createUser = (userData) => publicApi.post('/user/register', userData)
const uploadUserProfilePic = (file) => api.patch(`/user/picture-upload`, file, {
  headers: { 'content-type': 'multipart/form-data' },
})
const deleteUserProfilePicture = (publicId) => api.delete(`/user/${publicId}/delete-picture`)
const searchUsers = (q, role) => api.get(`/user/search`, { q, role })

// ─── LISTINGS ENDPOINTS ───────────────────────────────────────────
const presignVideo = () => api.post('/listings/uploads/presign-video')
const createListing = (formData) => api.post(`/listings/new-listing`, formData, {
  headers: { 'Content-Type': 'multipart/form-data' },
})
const createVideoListing = (formData) => api.post(`/listings/upload-video`, formData, {
  headers: { 'Content-Type': 'multipart/form-data' },
})
const createListingWithVideoUrl = (payload) => api.post('/listings/listing-with-video-url', payload)
const uploadVideoStatus = (jobId) => api.get(`/listings/${jobId}/upload-status`)
const getSingleListing = (id) => api.get(`/listings/${id}/listing`);
const getUserListings = (options) => api.get(`/listings/my-listings`, options)
const getAgentListings = (agentId, options) => api.get(`/listings/${agentId}/listings-of-agent`, options)
const publicGetAgentListings = (agentId, options) => publicApi.get(`/listings/${agentId}/listings-of-agent`, options)
const getAllListings = (options) => publicApi.get(`/listings/all-listings`, options)
const updateListingPrice = (listingId, price) =>
  api.patch(`/listings/${listingId}/listing-price?price=${price}`)
const updateAvailableRooms = (listingId, availableRooms) =>
  api.patch(`/listings/${listingId}/available-rooms?availableRooms=${availableRooms}`)
const updateVacancy = (listingId, vacancy, tillWhenDate) =>
  api.patch(`/listings/${listingId}/update-vacancy?vacancy=${vacancy}&tillWhenDate=${tillWhenDate}`)
const updateUnitVacancy = (listingId, unitId, vacancy, tillWhenDate) =>
  api.patch(`/listings/${listingId}/units/${unitId}/vacancy?vacancy=${vacancy}&tillWhenDate=${tillWhenDate}`)
const updateUnitPrice = (listingId, unitId, price) =>
  api.patch(`/listings/${listingId}/units/${unitId}/price?price=${price}`)
const uploadListingVideo = (listingId, s3Key, title) =>
  api.post(`/listings/${listingId}/upload-video?s3Key=${s3Key}&title=${title}`)
const updateListingVideoUrl = (listingId, videoUrl) =>
  api.patch(`/listings/${listingId}/update-video-url?videoUrl=${encodeURIComponent(videoUrl)}`)
const searchResults = (query, filters = {}, page = 0, size = 8) => {
  const params = { ...filters, page, size }
  if (query) params.q = query
  return api.get(`/listings/search`, params)
}
const publicSearchResults = (query, filters = {}, page = 0, size = 8) => {
  const params = { ...filters, page, size }
  if (query) params.q = query
  return publicApi.get(`/listings/search`, params)
}
const searcWithinAgentListings = (agentId, query, filters = {}, page = 0, size = 8) => {
  const params = { ...filters, page, size }
  if (query) params.q = query
  return api.get(`/listings/${agentId}/searchAgentListing`, params)
}
const publicSearchAgentListings = (agentId, query, filters = {}, page = 0, size = 8) => {
  const params = { ...filters, page, size }
  if (query) params.q = query
  return publicApi.get(`/listings/${agentId}/searchAgentListing`, params)
}

const searchListingsByDateRange = (vacancy, tillWhenDateFrom, tillWhenDateTo, page = 0, size = 10) =>
  api.get(`/search`, { vacancy, tillWhenDateFrom, tillWhenDateTo, page, size })

// ─── BOOKINGS ENDPOINTS ───────────────────────────────────────────
const createBooking = (data) => api.post(`/bookings/new-booking`, data)
const getMyBookings = (options) => api.get(`/bookings/user/all-my-bookings`, options)
const getMyAppointments = (options) => api.get(`/bookings/user/listing-appointments`, options)
const confirmBooking = (bookingId) => api.patch(`/bookings/${bookingId}/confirm-booking-status`)
const cancelBooking = (bookingId) => api.patch(`/bookings/${bookingId}/cancel-confirmed-booking`)

// ─── REWARD POINTS ──────────────────────────────────────────────────────
const rewardPoints =()=> api.get(`/points/balance`) ;
const redeemPoints =()=> api.get(`/points/redeem`) ;
const rewardPointsHistory =(page = 0, size = 10)=> api.get(`/points/history`, { page, size }) ;

// ─── YOUTUBE ────────────────────────────────────────────────────────────
const getYouTubeQuota = () => api.get('/youtube/quota')

// ─── USER RULES ──────────────────────────────────────────────────────────
const createUserRules = (data) => api.post('/user-rules', data)
const getUserRules = () => api.get('/user-rules')
const updateUserRules = (data) => api.patch('/user-rules', data)

// ─── VIEWER REQUESTS ─────────────────────────────────────────────────────
const createViewerRequest = (data) => api.post('/viewer-requests', data)
const getViewerRequests = (options) => api.get('/viewer-requests', options)

// ─── AMBASSADOR ─────────────────────────────────────────────────────────
const applyAmbassador = (data) => api.post('/ambassadors/apply', data)
const getAmbassadorApplicationStatus = () => api.get('/ambassadors/apply/status')
const getAmbassadorLeads = (options) => api.get('/ambassadors/leads', options)
const getAllVerifiedLeads = (options) => api.get('/leads/all-verified-leads', options)
const searchLeads = (query, filters = {}, page = 0, size = 8) => {
  const params = { ...filters, page, size }
  if (query) params.q = query
  return api.get('/leads/search', params)
}
const createAmbassadorLead = (data) => api.post('/ambassadors/leads', data)
const getAmbassadorLead = (id) => api.get(`/ambassadors/leads/${id}`)
const getLead = (id) => api.get(`/leads/${id}`)
const createLeadEnquiry = (data) => api.post('/leads/enquiries', data)
const getMyLeadEnquiries = (options) => api.get('/leads/enquiries/my', options)
const uploadLeadVideo = (leadId, s3Key, title) =>
  api.post(`/ambassadors/leads/${leadId}/upload-video?s3Key=${s3Key}&title=${title}`)
const createLeadReview = (leadId, data) => api.post(`/leads/${leadId}/reviews`, data)
const getLeadReviews = (leadId, options) => api.get(`/leads/${leadId}/reviews`, options)

// ─── TENANTS ─────────────────────────────────────────────────────────────
const createTenant = (listingId, data) => api.post(`/properties/${listingId}/tenants`, data)

// ─── PAYMENTS ─────────────────────────────────────────────────────────────
const initializePayment = (amount, bookingId = null, listingId = null) =>
  api.post('/payments/initialize', { amount, bookingId, listingId })
const getMyPayments = (options) => api.get('/payments/my', options)

// ─── FAVORITES ────────────────────────────────────────────────────────────
const addFavoriteListing = (listingId) => api.post(`/favorites/listings/${listingId}`)
const getFavoriteListing = (listingId) => api.get(`/favorites/listings/${listingId}`)
const getFavoriteListings = (options) => api.get(`/favorites/listings`, options)
const removeFavoriteListing = (listingId) => api.delete(`/favorites/listings/${listingId}`)
const addFavoriteLead = (leadId) => api.post(`/favorites/leads/${leadId}`)
const getFavoriteLeads = (options) => api.get(`/favorites/leads`, options)
const removeFavoriteLead = (leadId) => api.delete(`/favorites/leads/${leadId}`)

// ─── LISTING REVIEWS ──────────────────────────────────────────────────────
const createListingReview = (listingId, data) => api.post(`/listings/${listingId}/reviews`, data)
const getListingReviews = (listingId, options) => api.get(`/listings/${listingId}/reviews`, options)

// ─── PUBLIC STATS ─────────────────────────────────────────────────────────
const getPublicStats = (options) => publicStatsApi.get('/api/public/stats', options)

// ─── EXPORTS ──────────────────────────────────────────────────────
const endpoints = {
  setAuthToken,
  removeAuthToken,
  fetchUserBySub,
  updateUserBySub,
  createUser,
  presignVideo,
  createListing,
  createVideoListing,
  createListingWithVideoUrl,
  getUserListings,
  getSingleListing,
  getAgentListings,
  publicGetAgentListings,
  getAllListings,
  updateVacancy,
  updateListingPrice,
  updateAvailableRooms,
  updateUnitVacancy,
  updateUnitPrice,
  uploadListingVideo,
  updateListingVideoUrl,
  uploadUserProfilePic,
  deleteUserProfilePicture,
  searchUsers,
  searchResults,
  publicSearchResults,
  searcWithinAgentListings,
  publicSearchAgentListings,
  searchListingsByDateRange,
  createBooking,
  getMyBookings,
  getMyAppointments,
  confirmBooking,
  cancelBooking,
  uploadVideoStatus,
  rewardPointsHistory,
  rewardPoints,
  redeemPoints,
  getYouTubeQuota,
  createUserRules,
  getUserRules,
  updateUserRules,
  createViewerRequest,
  getViewerRequests,
  createTenant,
  initializePayment,
  getMyPayments,
  applyAmbassador,
  getAmbassadorApplicationStatus,
  getAmbassadorLeads,
  getAllVerifiedLeads,
  searchLeads,
  createAmbassadorLead,
  getAmbassadorLead,
  getLead,
  uploadLeadVideo,
  createLeadEnquiry,
  getMyLeadEnquiries,
  createLeadReview,
  getLeadReviews,
  addFavoriteListing,
  getFavoriteListing,
  getFavoriteListings,
  removeFavoriteListing,
  addFavoriteLead,
  getFavoriteLeads,
  removeFavoriteLead,
  createListingReview,
  getListingReviews,
  getPublicStats,
}

export default endpoints